import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/hooks/use-toast';
import {
  closeSmartValve,
  createSmartValveDevice,
  createSmartValveHub,
  createSmartValveUnit,
  createSmartValveZone,
  createYoLinkAccount,
  getSmartValveChoices,
  getSmartValveAreaTags,
  getSmartValveDevices,
  getSmartValveEvents,
  getSmartValveHubs,
  getSmartValveSummary,
  getSmartValveUnits,
  getSmartValveZones,
  getYoLinkAccounts,
  openSmartValve,
  refreshSmartValveHub,
  refreshSmartValveDevice,
  syncSmartValveZone,
  testSmartValveZoneCredentials,
  updateCommissioningTask,
  updateSmartValveUnit,
} from '../api/smart-valves-api';
import type {
  CreateSmartValveDeviceData,
  CreateSmartValveHubData,
  CreateSmartValveUnitData,
  CreateSmartValveZoneData,
  CreateYoLinkAccountData,
  UpdateCommissioningTaskData,
} from '../types';

type SmartValveApiError = {
  message?: string;
  status?: number;
  data?: {
    detail?: string;
    code?: string;
    vendor_detail?: string;
    payload?: {
      desc?: string;
      code?: string;
    };
  };
};

const smartValveErrorMessage = (error: unknown) => {
  const apiError = error as SmartValveApiError;
  const detail = apiError?.data?.detail || apiError?.message || 'The smart valve request failed.';
  const code = apiError?.data?.code || apiError?.data?.payload?.code;
  const vendorDescription = apiError?.data?.payload?.desc || apiError?.data?.vendor_detail;

  return [detail, code ? `YoLink code: ${code}` : '', vendorDescription && vendorDescription !== detail ? vendorDescription : '']
    .filter(Boolean)
    .join(' ');
};

export const smartValveKeys = {
  all: ['smart-valves'] as const,
  summary: (projectId?: number) => [...smartValveKeys.all, 'summary', projectId] as const,
  choices: () => [...smartValveKeys.all, 'choices'] as const,
  areaTags: (projectId?: number) => [...smartValveKeys.all, 'area-tags', projectId] as const,
  accounts: (projectId?: number) => [...smartValveKeys.all, 'accounts', projectId] as const,
  zones: (projectId?: number) => [...smartValveKeys.all, 'zones', projectId] as const,
  hubs: (projectId?: number) => [...smartValveKeys.all, 'hubs', projectId] as const,
  units: (projectId?: number) => [...smartValveKeys.all, 'units', projectId] as const,
  devices: (projectId?: number) => [...smartValveKeys.all, 'devices', projectId] as const,
  events: (projectId?: number) => [...smartValveKeys.all, 'events', projectId] as const,
};

export const useSmartValveSummary = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.summary(projectId),
    queryFn: () => getSmartValveSummary(projectId),
    staleTime: 30_000,
  });

export const useSmartValveChoices = () =>
  useQuery({
    queryKey: smartValveKeys.choices(),
    queryFn: getSmartValveChoices,
    staleTime: 10 * 60_000,
  });

export const useSmartValveAreaTags = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.areaTags(projectId),
    queryFn: () => getSmartValveAreaTags(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useSmartValveZones = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.zones(projectId),
    queryFn: () => getSmartValveZones(projectId),
    staleTime: 30_000,
  });

export const useYoLinkAccounts = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.accounts(projectId),
    queryFn: () => getYoLinkAccounts(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useSmartValveHubs = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.hubs(projectId),
    queryFn: () => getSmartValveHubs(projectId),
    staleTime: 30_000,
  });

export const useSmartValveUnits = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.units(projectId),
    queryFn: () => getSmartValveUnits(projectId),
    staleTime: 30_000,
  });

export const useSmartValveDevices = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.devices(projectId),
    queryFn: () => getSmartValveDevices(projectId),
    staleTime: 30_000,
  });

export const useSmartValveEvents = (projectId?: number) =>
  useQuery({
    queryKey: smartValveKeys.events(projectId),
    queryFn: () => getSmartValveEvents(projectId),
    staleTime: 15_000,
  });

export const useSmartValveMutations = (projectId?: number) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidateProject = () => {
    queryClient.invalidateQueries({ queryKey: smartValveKeys.summary(projectId) });
    queryClient.invalidateQueries({ queryKey: smartValveKeys.accounts(projectId) });
    queryClient.invalidateQueries({ queryKey: smartValveKeys.zones(projectId) });
    queryClient.invalidateQueries({ queryKey: smartValveKeys.hubs(projectId) });
    queryClient.invalidateQueries({ queryKey: smartValveKeys.units(projectId) });
    queryClient.invalidateQueries({ queryKey: smartValveKeys.devices(projectId) });
    queryClient.invalidateQueries({ queryKey: smartValveKeys.events(projectId) });
  };

  const showMutationError = (title: string) => (error: unknown) => {
    invalidateProject();
    toast({
      variant: 'destructive',
      title,
      description: smartValveErrorMessage(error),
    });
  };

  const createAccount = useMutation({
    mutationFn: (payload: CreateYoLinkAccountData) => createYoLinkAccount(payload),
    onSuccess: invalidateProject,
  });

  const createZone = useMutation({
    mutationFn: (payload: CreateSmartValveZoneData) => createSmartValveZone(payload),
    onSuccess: invalidateProject,
  });

  const createHub = useMutation({
    mutationFn: (payload: CreateSmartValveHubData) => createSmartValveHub(payload),
    onSuccess: invalidateProject,
  });

  const testCredentials = useMutation({
    mutationFn: (zoneId: number) => testSmartValveZoneCredentials(zoneId),
    onSuccess: invalidateProject,
  });

  const syncZone = useMutation({
    mutationFn: (zoneId: number) => syncSmartValveZone(zoneId),
    onSuccess: invalidateProject,
  });

  const createUnit = useMutation({
    mutationFn: (payload: CreateSmartValveUnitData) => createSmartValveUnit(payload),
    onSuccess: invalidateProject,
  });

  const updateUnit = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Parameters<typeof updateSmartValveUnit>[1] }) =>
      updateSmartValveUnit(id, payload),
    onSuccess: invalidateProject,
  });

  const createDevice = useMutation({
    mutationFn: (payload: CreateSmartValveDeviceData) => createSmartValveDevice(payload),
    onSuccess: invalidateProject,
  });

  const refreshDevice = useMutation({
    mutationFn: (id: number) => refreshSmartValveDevice(id),
    onSuccess: invalidateProject,
    onError: showMutationError('Device sync failed'),
  });

  const refreshHub = useMutation({
    mutationFn: (id: number) => refreshSmartValveHub(id),
    onSuccess: invalidateProject,
    onError: showMutationError('Hub sync failed'),
  });

  const openValve = useMutation({
    mutationFn: (id: number) => openSmartValve(id),
    onSuccess: invalidateProject,
    onError: showMutationError('Open command failed'),
  });

  const closeValve = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      closeSmartValve(id, { create_test_record: true, notes }),
    onSuccess: invalidateProject,
    onError: showMutationError('Close command failed'),
  });

  const updateTask = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateCommissioningTaskData }) =>
      updateCommissioningTask(id, payload),
    onSuccess: invalidateProject,
  });

  return {
    createAccount,
    createZone,
    createHub,
    testCredentials,
    syncZone,
    createUnit,
    updateUnit,
    createDevice,
    refreshDevice,
    refreshHub,
    openValve,
    closeValve,
    updateTask,
  };
};
